/**
 * Data hook for the comparison strips: KJV per-word English + Strong's
 * from verse-study, NASB native script from the static Strong's JSON.
 */

import { useEffect, useState } from 'react';
import type { BibleLocation, VariantAnalysis } from '@skjv/common';
import { formatReference } from '@skjv/common';
import { fetchVerseStudy, fetchMultipleStrongs } from '@api/index';
import { buildKjvCells, alignSequences, type AlignedSlot } from './alignment';

export interface ComparisonData {
  slots: AlignedSlot[];
  /** number → English gloss (for NASB cells; the NASB DB has no English,
   *  so we render the Strong's-definition translation gloss instead) */
  glosses: Map<string, string>;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Extract a short English gloss from a Strong's definition — the KJV
 * translation list after ":--" (e.g. ":--adorning, world."). Falls back
 * to the native script when the definition has no gloss tail.
 */
export function glossFromDefinition(definition: string, native: string): string {
  const match = definition.match(/:--(.+)$/s);
  if (!match) return native;
  const seen = new Set<string>();
  const items = match[1]
    .replace(/<[\s\S]*$/, '') // drop everything from the first HTML tag (cross-ref links)
    .replace(/\bsee(\s+also)?\s*$/i, '') // and the dangling "See (also)" that preceded it
    .replace(/\([\s\S]*?\)/g, '') // strip parentheticals BEFORE splitting — they contain commas/newlines
    .split(/[,;]/)
    .map((s) => s.trim().replace(/\.$/, ''))
    .filter((s) => s && !/^[+X](\s|$)/.test(s)) // drop Strong's idiom-marked renderings
    .filter((s) => {
      const key = s.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 2);
  return items.length ? items.join(', ') : native;
}

export function useComparisonData(
  analysis: VariantAnalysis,
  location: BibleLocation,
): ComparisonData {
  const [data, setData] = useState<ComparisonData>({
    slots: [],
    glosses: new Map(),
    isLoading: true,
    hasError: false,
  });

  const refKey = formatReference(location);

  useEffect(() => {
    let cancelled = false;
    setData({ slots: [], glosses: new Map(), isLoading: true, hasError: false });

    const load = async () => {
      const study = await fetchVerseStudy(refKey, 'verse');
      const words = study.verses[0]?.words ?? [];
      const kjvCells = buildKjvCells(words);
      const slots = alignSequences(kjvCells, analysis.nasbNumbers);

      const uniqueNumbers = [...new Set([...kjvCells.map((c) => c.number), ...analysis.nasbNumbers])];
      const defs = await fetchMultipleStrongs(uniqueNumbers);
      const glosses = new Map<string, string>();
      defs.forEach((def, i) => {
        if (def) {
          glosses.set(
            uniqueNumbers[i],
            glossFromDefinition(def.metadata.definition, def.metadata.native),
          );
        }
      });

      if (!cancelled) setData({ slots, glosses, isLoading: false, hasError: false });
    };

    load().catch(() => {
      if (!cancelled) setData({ slots: [], glosses: new Map(), isLoading: false, hasError: true });
    });

    return () => {
      cancelled = true;
    };
  }, [refKey, analysis.reference]);

  return data;
}
